// るすの間サマリ。起動時のオフライン精算（settleOffline）の結果を
// 「るすの間に来た客」の置き手紙風に見せる（数字の羅列にしない方針）。

import { getCustomer } from '../sim/customers';
import { getDeco } from '../sim/decos';
import { STAGE_LABELS } from '../sim/plants';
import type { OfflineSummary } from '../sim/simulate';

export interface AwaySummaryPanel {
  /** サマリを表示する。来店ゼロなら何もしない（false を返す）。 */
  show(summary: OfflineSummary): boolean;
  close(): void;
}

export interface AwaySummaryOptions {
  rootEl: HTMLElement; // #away
  leadEl: HTMLElement; // #awayLead
  listEl: HTMLElement; // #awayList
}

/** 経過秒を「○時間○分」程度のやわらかい表現にする。 */
function humanizeDuration(sec: number): string {
  const m = Math.floor(sec / 60);
  if (m < 1) return 'ほんの少しの間';
  if (m < 60) return `${m}分ほど`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  if (h < 24) return rem > 0 ? `${h}時間${rem}分ほど` : `${h}時間ほど`;
  const d = Math.floor(h / 24);
  return `${d}日`;
}

export function createAwaySummaryPanel(opts: AwaySummaryOptions): AwaySummaryPanel {
  const { rootEl, leadEl, listEl } = opts;

  function close(): void {
    rootEl.classList.remove('open');
  }

  function show(summary: OfflineSummary): boolean {
    const grew = summary.growthEvents.length > 0;
    // 来店も収入の増加も成長も無いなら出さない（毎回出るとうるさい）。
    if (summary.totalVisits === 0 && summary.chipsGained < 1 && !grew) return false;

    leadEl.textContent =
      `${humanizeDuration(summary.elapsedSec)}のお留守の間に、` +
      `${summary.totalVisits} 組のお客さんが立ち寄っていきました。`;

    listEl.innerHTML = '';

    // 庭の成長（盆栽が段階を進めた）を先に置く（うれしい変化を最初に見せる）。
    for (const ev of summary.growthEvents) {
      const deco = getDeco(ev.decoId);
      const name = deco ? deco.name : '植物';
      const li = document.createElement('li');
      li.className = 'growth';
      li.textContent = `🌱 ${name}が ${STAGE_LABELS[ev.from]} から ${STAGE_LABELS[ev.to]} になりました`;
      listEl.appendChild(li);
    }

    const entries = Object.entries(summary.visitsByCustomer);
    if (entries.length === 0 && !grew) {
      const li = document.createElement('li');
      li.textContent = 'お客さんの足あとは無かったけれど、チップは少し貯まっていた。';
      listEl.appendChild(li);
    } else {
      for (const [id, n] of entries) {
        const def = getCustomer(id);
        const name = def ? def.name : id;
        const icon = def ? def.icon : '·';
        const isNew = summary.newlyDiscovered.includes(id);
        const li = document.createElement('li');
        li.textContent = `${icon} ${name} ×${n}${isNew ? '　🆕 はじめての来店！' : ''}`;
        listEl.appendChild(li);
      }
    }
    const gain = document.createElement('li');
    gain.textContent = `💰 ぜんぶで +${Math.floor(summary.chipsGained)} チップ`;
    listEl.appendChild(gain);

    // ゆらぎ（v0.6.0）: 今しおれているデコがあればそっと触れる（枯死ではない・じき戻る）。
    if (summary.hasWiltedNow) {
      const wilt = document.createElement('li');
      wilt.textContent = '🍄 光るキノコが少ししおれている…（じきにまた元気になるよ）';
      listEl.appendChild(wilt);
    }

    rootEl.classList.add('open');
    return true;
  }

  // 背景タップで閉じる
  rootEl.addEventListener('click', () => close());

  return { show, close };
}
